import { CONFIG } from "./config";
import { dayIndexOf, type GameState } from "./state";

// Bump this whenever GameState's shape changes in a way older saves won't
// satisfy (new required fields, renamed fields, etc.) — mismatched saves are
// discarded rather than loaded, so a schema change can't crash the game on
// boot by feeding old data into new code (see doc/game_design.md history:
// this bit a real playtest once already).
const SCHEMA_VERSION = 7;

const LEGACY_SAVE_KEY = "proto-power-grid-game-save"; // pre-multi-slot, single save
const INDEX_KEY = "proto-power-grid-game-saves-index";
const SLOT_KEY_PREFIX = "proto-power-grid-game-save:";
const AUTOSAVE_SLOT_ID = "autosave";

export interface SaveSlotMeta {
  id: string;
  name: string;
  savedAt: number; // Date.now()
  turn: number;
  day: number;
  score: number;
}

interface SavePayload {
  schemaVersion: number;
  state: GameState;
}

function slotKey(id: string): string {
  return SLOT_KEY_PREFIX + id;
}

function readIndex(): SaveSlotMeta[] {
  const raw = localStorage.getItem(INDEX_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeIndex(index: SaveSlotMeta[]): void {
  localStorage.setItem(INDEX_KEY, JSON.stringify(index));
}

function metaFromState(id: string, name: string, state: GameState): SaveSlotMeta {
  return {
    id,
    name,
    savedAt: Date.now(),
    turn: state.turn,
    day: dayIndexOf(state.turn, CONFIG.phasesPerDay) + 1,
    score: Math.round(state.score),
  };
}

/** One-time migration: a save from before multi-slot support existed is
 * adopted as the autosave slot instead of silently disappearing the first
 * time this runs against an old browser profile. */
function migrateLegacySaveIfPresent(): void {
  const raw = localStorage.getItem(LEGACY_SAVE_KEY);
  if (!raw) return;
  localStorage.removeItem(LEGACY_SAVE_KEY);
  try {
    const payload = JSON.parse(raw) as Partial<SavePayload>;
    if (payload.schemaVersion !== SCHEMA_VERSION || !payload.state) return; // incompatible — drop it, same as the old behavior did
    if (readIndex().some((s) => s.id === AUTOSAVE_SLOT_ID)) return; // already migrated, or autosave already exists
    saveGameToSlot(payload.state, AUTOSAVE_SLOT_ID, "Autosave");
  } catch {
    // corrupt legacy save — already removed above, nothing more to do
  }
}

/** Writes (or overwrites, if `id` already exists) one named slot. */
export function saveGameToSlot(state: GameState, id: string, name: string): void {
  const payload: SavePayload = { schemaVersion: SCHEMA_VERSION, state };
  localStorage.setItem(slotKey(id), JSON.stringify(payload));
  const index = readIndex();
  const meta = metaFromState(id, name, state);
  const existingIdx = index.findIndex((s) => s.id === id);
  if (existingIdx >= 0) index[existingIdx] = meta;
  else index.push(meta);
  writeIndex(index);
}

/** Creates a brand-new named slot (never overwrites) — used by the Saves
 * panel to snapshot "an interesting situation" without touching autosave. */
export function saveGameAsNewSlot(state: GameState, name: string): string {
  const id = `slot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  saveGameToSlot(state, id, name.trim() || "Untitled save");
  return id;
}

export function loadGameFromSlot(id: string): GameState | null {
  const raw = localStorage.getItem(slotKey(id));
  if (!raw) return null;
  try {
    const payload = JSON.parse(raw) as Partial<SavePayload>;
    if (payload.schemaVersion !== SCHEMA_VERSION || !payload.state) {
      deleteSaveSlot(id); // stale/incompatible — drop it so it can't crash a future load either
      return null;
    }
    return payload.state;
  } catch {
    deleteSaveSlot(id);
    return null;
  }
}

export function deleteSaveSlot(id: string): void {
  localStorage.removeItem(slotKey(id));
  writeIndex(readIndex().filter((s) => s.id !== id));
}

/** Every saved game (autosave included), newest first. */
export function listSaveSlots(): SaveSlotMeta[] {
  migrateLegacySaveIfPresent();
  return readIndex().sort((a, b) => b.savedAt - a.savedAt);
}

// --- autosave-slot convenience wrappers — same names/signatures the app
// already used before multi-slot support, so every existing call site
// (the "Next Turn"/"Save" buttons, boot-time load) keeps working unchanged
// and just targets the dedicated autosave slot. ---

export function saveGame(state: GameState): void {
  saveGameToSlot(state, AUTOSAVE_SLOT_ID, "Autosave");
}

export function loadGame(): GameState | null {
  migrateLegacySaveIfPresent();
  return loadGameFromSlot(AUTOSAVE_SLOT_ID);
}

export function clearSave(): void {
  deleteSaveSlot(AUTOSAVE_SLOT_ID);
}

export function hasSave(): boolean {
  migrateLegacySaveIfPresent();
  return localStorage.getItem(slotKey(AUTOSAVE_SLOT_ID)) !== null;
}
